import { useCallback, useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';
import {
  usePersonaMediaGeneration,
  type RetryableVideoType,
} from '@/features/audiences/model/usePersonaMediaGeneration';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import { useEditPersonaFormContext } from '../../../../hooks/useEditPersonaFormContext';
import type { BannerType, ImageMetadata, VideoMetadata } from '../../ProfileMediaTab.types';
import { DEFAULT_VIDEO_MODEL, DEFAULT_IMAGE_MODEL } from '@/features/audiences/model';

/**
 * For a content-filter refusal the API's reason names the input the user has to
 * change, which generic copy cannot.
 */
function failureReason(error: unknown): string | null {
  const { detail } = parseProblemDetail(error, '');
  return detail || null;
}

interface MediaFailure {
  label: string;
  reason: string | null;
}

function videoLabel(t: TFunction<'audiences'>, isThumbnail: boolean): string {
  return isThumbnail
    ? t('audiences.useProfileMediaTab.thumbnailVideo')
    : t('audiences.useProfileMediaTab.detailVideo');
}

function notifyFailure(title: string, description: string): void {
  notificationService.showError(title, {
    anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
    description,
    persist: true,
  });
}

function reasonOrFallback(t: TFunction<'audiences'>, reason: string | null): string {
  return reason ?? t('audiences.useProfileMediaTab.tryAgainAndIfTheError');
}

/**
 * One toast per generation, never one per request. Generating both videos fires
 * two calls that fail together, and a stacked paragraph each buried the guidance
 * instead of delivering it. Only videos reach the plural case; there the reason
 * stays on each card, which is where the user acts.
 */
function notifyVideoFailures(t: TFunction<'audiences'>, failures: MediaFailure[]): void {
  if (failures.length === 0) return;

  if (failures.length === 1) {
    const [only] = failures;
    notifyFailure(
      t('audiences.useProfileMediaTab.couldnTGenerateThe', { label: only.label }),
      reasonOrFallback(t, only.reason),
    );
    return;
  }

  // Pointing at the cards only helps when a card actually got a reason. A transport
  // failure (no Problem Detail body) leaves both showing generic copy, so fall back
  // to the wording a single failure would have given.
  const anyReason = failures.some((failure) => failure.reason !== null);

  notifyFailure(
    t('audiences.useProfileMediaTab.couldnTGenerateTheVideos'),
    anyReason
      ? t('audiences.useProfileMediaTab.seeTheMessageOnEachVideo')
      : reasonOrFallback(t, null),
  );
}

export function useProfileMediaTab() {
  const { t } = useTranslation('audiences');
  const { t: tCommon } = useTranslation('common');
  const form = useEditPersonaFormContext();
  const {
    personaId,
    setIsUploadingMedia,
    setIsGeneratingMedia,
    isProcessingMedia,
    isUploadingMedia,
  } = form;

  const {
    regenerateImage,
    isRegeneratingImage,
    generateVideos,
    isGeneratingVideos,
    retryVideo,
    isRetryingVideo,
  } = usePersonaMediaGeneration({
    personaId,
  });

  useEffect(() => {
    setIsGeneratingMedia(isRegeneratingImage || isGeneratingVideos || isRetryingVideo);
  }, [isRegeneratingImage, isGeneratingVideos, isRetryingVideo, setIsGeneratingMedia]);

  // Form field controllers for asset IDs
  const { field: photoField } = useController({ control: form.control, name: 'photoAssetId' });
  const { field: mouseOverField } = useController({
    control: form.control,
    name: 'mouseOverAssetId',
  });
  const { field: videoField } = useController({ control: form.control, name: 'videoAssetId' });

  // UI state
  const [showBanner, setShowBanner] = useState(false);
  const [bannerType, setBannerType] = useState<BannerType | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Prompt state for modals
  const [imagePrompt, setImagePrompt] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [selectedImageModel, setSelectedImageModel] = useState(DEFAULT_IMAGE_MODEL);
  const [selectedVideoModel, setSelectedVideoModel] = useState(DEFAULT_VIDEO_MODEL);

  const processingLabel = isUploadingMedia
    ? tCommon('components.imageSourcePicker.uploading')
    : isRegeneratingImage
      ? t('audiences.videoSection.generating')
      : undefined;

  const isProcessingImage = isUploadingMedia || isRegeneratingImage;

  /**
   * TODO(DECISION-2): Metadata currently session-only via useState.
   * If backend persists metadata in persona entity, switch to reading from DTO instead.
   */
  const [imageMetadata, setImageMetadata] = useState<ImageMetadata | null>(null);
  const [thumbnailMetadata, setThumbnailMetadata] = useState<VideoMetadata | null>(null);
  const [detailMetadata, setDetailMetadata] = useState<VideoMetadata | null>(null);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);
  const [thumbnailErrorMessage, setThumbnailErrorMessage] = useState<string | null>(null);
  const [detailErrorMessage, setDetailErrorMessage] = useState<string | null>(null);
  const [retryingVideoType, setRetryingVideoType] = useState<RetryableVideoType | null>(null);
  const [lastVideoModel, setLastVideoModel] = useState(DEFAULT_VIDEO_MODEL);
  const [lastVideoPrompt, setLastVideoPrompt] = useState<string | undefined>(undefined);

  // Initialize metadata for existing persona images
  useEffect(() => {
    if (photoField.value && !imageMetadata) {
      setImageMetadata({ source: 'user' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

  const resetVideoState = useCallback(() => {
    setThumbnailMetadata(null);
    setDetailMetadata(null);
    setThumbnailFailed(false);
    setDetailFailed(false);
    setThumbnailErrorMessage(null);
    setDetailErrorMessage(null);
  }, []);

  // File input ref for image upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Upload flow for profile image.
   * Sequence: getFileUploadUrl → S3 POST → field.onChange(assetId)
   */
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploadingMedia(true);
      try {
        const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name, {
          scope: 'organization',
        });

        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', file);

        await uploadFileToS3(url, formData);
        photoField.onChange(assetId);
        mouseOverField.onChange(null);
        videoField.onChange(null);
        resetVideoState();

        setImageMetadata({
          source: 'user',
          createdAt: new Date().toLocaleDateString(),
        });

        setBannerType('upload');
        setShowBanner(true);
      } catch (error) {
        logger.error('Failed to upload media file', error);
        notificationService.showError(t('audiences.useProfileMediaTab.failedToUploadImage'), {
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          description: t('audiences.useProfileMediaTab.tryAgainAndIfTheError'),
        });
      } finally {
        setIsUploadingMedia(false);
        event.target.value = '';
      }
    },
    [photoField, mouseOverField, videoField, setIsUploadingMedia, resetVideoState, t],
  );

  const handleImageRegenerateClick = useCallback(() => {
    setImageModalOpen(true);
  }, []);

  const handleVideoRegenerateClick = useCallback(() => {
    setVideoModalOpen(true);
  }, []);

  /**
   * Generate videos (mouseOver + detail) in parallel from empty state.
   * Uses the mutation from usePersonaMediaGeneration.
   * Handles partial success - one video can fail while other succeeds.
   */
  const handleVideoGenerateClick = useCallback(async () => {
    setLastVideoModel(selectedVideoModel);
    setLastVideoPrompt(undefined);
    try {
      const result = await generateVideos({
        model: selectedVideoModel,
        photoAssetId: photoField.value ?? undefined,
      });
      const now = new Date().toLocaleDateString();
      const failures: MediaFailure[] = [];

      // Handle thumbnail video (mouseOver)
      if (result.mouseOverAssetId) {
        mouseOverField.onChange(result.mouseOverAssetId);
        setThumbnailFailed(false);
        setThumbnailErrorMessage(null);
        setThumbnailMetadata({
          source: 'agent',
          modelUsed: selectedVideoModel,
          createdAt: now,
          videoType: 'thumbnail',
        });
      }
      if (result.mouseOverError) {
        logger.error('Failed to generate thumbnail video', result.mouseOverError);
        const reason = failureReason(result.mouseOverError);
        setThumbnailFailed(true);
        setThumbnailErrorMessage(reason);
        failures.push({ label: videoLabel(t, true), reason });
      }

      // Handle detail video
      if (result.videoAssetId) {
        videoField.onChange(result.videoAssetId);
        setDetailFailed(false);
        setDetailErrorMessage(null);
        setDetailMetadata({
          source: 'agent',
          modelUsed: selectedVideoModel,
          createdAt: now,
          videoType: 'detail',
        });
      }
      if (result.videoError) {
        logger.error('Failed to generate detail video', result.videoError);
        const reason = failureReason(result.videoError);
        setDetailFailed(true);
        setDetailErrorMessage(reason);
        failures.push({ label: videoLabel(t, false), reason });
      }

      notifyVideoFailures(t, failures);

      logger.info('Video generation completed', {
        mouseOverAssetId: result.mouseOverAssetId,
        videoAssetId: result.videoAssetId,
      });
    } catch (error) {
      logger.error('Failed to generate videos', error);
      const reason = failureReason(error);
      setThumbnailFailed(true);
      setDetailFailed(true);
      setThumbnailErrorMessage(reason);
      setDetailErrorMessage(reason);
      notifyVideoFailures(t, [
        { label: videoLabel(t, true), reason },
        { label: videoLabel(t, false), reason },
      ]);
    }
  }, [generateVideos, mouseOverField, videoField, selectedVideoModel, photoField.value]);

  const handleImageSend = useCallback(async () => {
    const prompt = imagePrompt.trim() || undefined;
    const model = selectedImageModel;
    setImageModalOpen(false);

    try {
      const result = await regenerateImage({ prompt, model });

      photoField.onChange(result.assetId);
      mouseOverField.onChange(null);
      videoField.onChange(null);
      resetVideoState();

      setImageMetadata({
        source: 'agent',
        promptUsed: prompt,
        modelUsed: model,
        createdAt: new Date().toLocaleDateString(),
      });

      setBannerType('generation');
      setShowBanner(true);
    } catch (error) {
      logger.error('Failed to generate image', error);
      // The image section has no inline error surface, so this toast is the only
      // place the API's reason can reach the user.
      notifyFailure(
        t('audiences.useProfileMediaTab.couldnTGenerateTheImage'),
        reasonOrFallback(t, failureReason(error)),
      );
    }
  }, [
    imagePrompt,
    selectedImageModel,
    regenerateImage,
    photoField,
    mouseOverField,
    videoField,
    resetVideoState,
    t,
  ]);

  /**
   * Regenerate videos with custom prompt (from modal).
   * Prompt only applies to detail video; thumbnail regenerates with default settings.
   * Handles partial success - one video can fail while other succeeds.
   */
  const handleVideoSend = useCallback(async () => {
    const prompt = videoPrompt.trim() || undefined;
    const model = selectedVideoModel;
    setVideoModalOpen(false);
    setLastVideoModel(model);
    setLastVideoPrompt(prompt);

    try {
      const result = await generateVideos({
        model,
        prompt,
        photoAssetId: photoField.value ?? undefined,
      });
      const now = new Date().toLocaleDateString();
      const failures: MediaFailure[] = [];

      // Handle thumbnail video (mouseOver) - no custom prompt, uses default settings
      if (result.mouseOverAssetId) {
        mouseOverField.onChange(result.mouseOverAssetId);
        setThumbnailFailed(false);
        setThumbnailErrorMessage(null);
        setThumbnailMetadata({
          source: 'agent',
          modelUsed: model,
          createdAt: now,
          videoType: 'thumbnail',
        });
      }
      if (result.mouseOverError) {
        logger.error('Failed to generate thumbnail video', result.mouseOverError);
        const reason = failureReason(result.mouseOverError);
        setThumbnailFailed(true);
        setThumbnailErrorMessage(reason);
        failures.push({ label: videoLabel(t, true), reason });
      }

      // Handle detail video - with prompt
      if (result.videoAssetId) {
        videoField.onChange(result.videoAssetId);
        setDetailFailed(false);
        setDetailErrorMessage(null);
        setDetailMetadata({
          source: 'agent',
          modelUsed: model,
          promptUsed: prompt,
          createdAt: now,
          videoType: 'detail',
        });
      }
      if (result.videoError) {
        logger.error('Failed to generate detail video', result.videoError);
        const reason = failureReason(result.videoError);
        setDetailFailed(true);
        setDetailErrorMessage(reason);
        failures.push({ label: videoLabel(t, false), reason });
      }

      notifyVideoFailures(t, failures);
    } catch (error) {
      logger.error('Failed to generate videos', error);
      const reason = failureReason(error);
      setThumbnailFailed(true);
      setDetailFailed(true);
      setThumbnailErrorMessage(reason);
      setDetailErrorMessage(reason);
      notifyVideoFailures(t, [
        { label: videoLabel(t, true), reason },
        { label: videoLabel(t, false), reason },
      ]);
    }
  }, [
    videoPrompt,
    selectedVideoModel,
    generateVideos,
    mouseOverField,
    videoField,
    photoField.value,
  ]);

  /**
   * Retry a single failed video (thumbnail or detail) reusing the model and prompt
   * from the last generation. Only the failed video is regenerated.
   */
  const handleRetryVideo = useCallback(
    async (type: RetryableVideoType) => {
      setRetryingVideoType(type);
      try {
        const result = await retryVideo({
          type,
          model: lastVideoModel,
          prompt: lastVideoPrompt,
          photoAssetId: photoField.value ?? undefined,
        });
        const now = new Date().toLocaleDateString();

        if (type === 'mouseOverVideo') {
          mouseOverField.onChange(result.assetId);
          setThumbnailFailed(false);
          setThumbnailErrorMessage(null);
          setThumbnailMetadata({
            source: 'agent',
            modelUsed: lastVideoModel,
            createdAt: now,
            videoType: 'thumbnail',
          });
        } else {
          videoField.onChange(result.assetId);
          setDetailFailed(false);
          setDetailErrorMessage(null);
          setDetailMetadata({
            source: 'agent',
            modelUsed: lastVideoModel,
            promptUsed: lastVideoPrompt,
            createdAt: now,
            videoType: 'detail',
          });
        }
      } catch (error) {
        logger.error(`Failed to retry ${type}`, error);
        const reason = failureReason(error);
        const isThumbnail = type === 'mouseOverVideo';
        if (isThumbnail) {
          setThumbnailFailed(true);
          setThumbnailErrorMessage(reason);
        } else {
          setDetailFailed(true);
          setDetailErrorMessage(reason);
        }
        notifyVideoFailures(t, [{ label: videoLabel(t, isThumbnail), reason }]);
      } finally {
        setRetryingVideoType(null);
      }
    },
    [retryVideo, lastVideoModel, lastVideoPrompt, mouseOverField, videoField, photoField.value, t],
  );

  const handleBannerClose = useCallback(() => {
    setShowBanner(false);
    setBannerType(null);
  }, []);

  const handleBannerGenerateVideos = useCallback(() => {
    setShowBanner(false);
    setBannerType(null);
    void handleVideoGenerateClick();
  }, [handleVideoGenerateClick]);

  return {
    // Form fields
    photoAssetId: photoField.value,
    mouseOverAssetId: mouseOverField.value,
    videoAssetId: videoField.value,
    // Processing
    isProcessingMedia,
    isProcessingImage,
    isRegeneratingImage,
    isGeneratingVideos,
    processingLabel,
    // Banner
    showBanner,
    bannerType,
    handleBannerClose,
    handleBannerGenerateVideos,
    // Image section
    imageMetadata,
    handleUploadClick,
    handleImageRegenerateClick,
    // Video section
    thumbnailMetadata,
    detailMetadata,
    handleVideoRegenerateClick,
    handleVideoGenerateClick,
    thumbnailFailed,
    detailFailed,
    thumbnailErrorMessage,
    detailErrorMessage,
    retryingVideoType,
    handleRetryVideo,
    // File input
    fileInputRef,
    handleFileChange,
    // Image modal
    imageModalOpen,
    setImageModalOpen,
    imagePrompt,
    setImagePrompt,
    selectedImageModel,
    setSelectedImageModel,
    handleImageSend,
    // Video modal
    videoModalOpen,
    setVideoModalOpen,
    videoPrompt,
    setVideoPrompt,
    selectedVideoModel,
    setSelectedVideoModel,
    handleVideoSend,
  };
}
